import { CircularProgress } from '@mui/material';
import { useState } from 'react';
import { ButtonWrapper, SearchInputWrapper, StyledButton } from './styled';
import { useSchoolClassesConfigHook } from '../common/hooks/useSchoolClassesConfig';
import DropdownSelect from '../../components/DropdownSelect';
import { academicYears } from '../../constants/academicYears';
import { SnackbarSeverityType } from '../../enums';
import ChequeDetailReportTable from './components/ChequeDetailReportTable';
import {
  fetchChequeDetailReportData,
  transformChequeDetailReportData
} from './utils/transformChequeDetailReportData';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import ToastMessage from '../../components/ToastMessage';
import { downloadReport } from '../../utils/reportUtils';
import { particularOptions, statusOptions } from './constants';
import { PaperWrapper, Title } from '../common/styled';

const buildFilters = (
  status: string,
  collectionType: string,
  grade: string,
  academicYear: string
) => ({
  chequeStatus: status === 'All' ? '' : status,
  collectionType: collectionType === 'All' ? '' : collectionType,
  grade: grade === 'All' ? '' : grade,
  academicYear: academicYear === 'All' ? '' : academicYear
});

const getReportName = (status: string) => {
  switch (status) {
    case 'BOUNCED':
      return 'ChequeBouncedReport';
    case 'CLEARED':
      return 'ChequeClearedReport';
    case 'RECEIVED':
      return 'ChequeNotDepositedReport';
    default:
      return 'ChequeDetailReport';
  }
};

const ChequeDetailReportPage = () => {
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const school = loggedInUser.school || '';

  const [academicYear, setAcademicYear] = useState<string>('All');
  const [grade, setGrade] = useState<string>('All');
  const [collectionType, setCollectionType] = useState<string>('All');
  const [status, setStatus] = useState<string>('All');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );
  const [chequeDetailReportData, setChequeDetailReportData] = useState([]);
  const { gradeOptions } = useSchoolClassesConfigHook();
  const gradeOptionsValues = [{ value: 'All', label: 'All' }, ...gradeOptions];
  const academicYearOptions = [
    { value: 'All', label: 'All' },
    ...academicYears
  ];

  const handleSearch = async () => {
    setIsSearching(true);
    setChequeDetailReportData([]);
    try {
      const filters = buildFilters(status, collectionType, grade, academicYear);
      const response = await fetchChequeDetailReportData(filters);
      const transformedData = transformChequeDetailReportData(response);
      setChequeDetailReportData(transformedData);
      showToast(
        'Successfully fetched cheque detail report',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Error fetching cheque detail report',
        SnackbarSeverityType.error
      );
    } finally {
      setIsSearching(false);
      setHasSearched(true);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const filters = buildFilters(status, collectionType, grade, academicYear);
      const response = await fetchChequeDetailReportData(filters);
      const transformedData = transformChequeDetailReportData(response);
      if (isEmpty(transformedData)) {
        showToast(
          'There is no data available to download as an Excel file.',
          SnackbarSeverityType.error
        );
        return;
      }

      let reportName = getReportName(status);

      const fileName = `${school}-${reportName}`;

      downloadReport(
        transformedData,
        fileName,
        'Cheque Detail Report',
        showToast
      );

      showToast('Report downloaded successfully', SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Error downloading cheque detail report',
        SnackbarSeverityType.error
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastOpen(true);
    setToastMessage(message);
    setToastSeverity(severity);
  };

  return (
    <>
      <Title>Cheque Detail Report</Title>
      <PaperWrapper sx={{ minHeight: 'auto' }}>
        <SearchInputWrapper>
          <DropdownSelect
            label='Academic Year'
            placeholder='Select Academic Year'
            name='academicYear'
            options={academicYearOptions}
            value={academicYear}
            onChange={(event) => {
              setAcademicYear(event.target.value as string);
              setHasSearched(false);
            }}
            hasTopLabel
          />
          <DropdownSelect
            label='Grade'
            placeholder='Select Grade'
            name='grade'
            options={gradeOptionsValues}
            value={grade}
            onChange={(event) => {
              setGrade(event.target.value as string);
              setHasSearched(false);
            }}
            hasTopLabel
          />
          <DropdownSelect
            label='Collection Type'
            placeholder='Select Collection Type'
            name='collectionType'
            options={[{ label: 'All', value: 'All' }, ...particularOptions]}
            value={collectionType}
            onChange={(event) => {
              setCollectionType(event.target.value as string);
              setHasSearched(false);
            }}
            hasTopLabel
          />
          <DropdownSelect
            label='Status'
            placeholder='Select Status'
            name='status'
            options={statusOptions}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as string);
              setHasSearched(false);
            }}
            hasTopLabel
          />
        </SearchInputWrapper>
        <ButtonWrapper>
          <StyledButton onClick={handleSearch}>
            {isSearching ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'View Report'
            )}
          </StyledButton>
          <StyledButton onClick={handleDownload} disabled={isSearching}>
            {isDownloading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Download Report'
            )}
          </StyledButton>
        </ButtonWrapper>
      </PaperWrapper>
      {hasSearched && (
        <ChequeDetailReportTable
          chequeDetailReportData={chequeDetailReportData}
        />
      )}

      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastOpen(false)}
      />
    </>
  );
};

export default ChequeDetailReportPage;
